import { ImagePlus, X } from 'lucide-react'
import { useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from '@/components/ui/field'
import { useFieldContext } from '@/lib/forms/context'

interface FileFieldProps {
  label?: string
  description?: string
  accept?: string
  className?: string
}

export function FileField({
  label,
  description,
  accept = 'image/*',
  className,
}: FileFieldProps) {
  const field = useFieldContext<File>()

  const [preview, setPreview] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      field.handleChange(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemove = () => {
    field.handleChange(null as any)
    setPreview(null)
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  return (
    <Field className={className}>
      {label && (
        <FieldLabel className="text-foreground text-base font-bold">
          {label}
        </FieldLabel>
      )}
      <FieldContent>
        <div className="relative">
          <input
            type="file"
            ref={inputRef}
            aria-label={label ?? 'เลือกไฟล์'}
            className="hidden"
            accept={accept}
            onChange={handleFileChange}
          />

          {!preview ? (
            <Button
              type="button"
              variant="outline"
              className="bg-background/50 hover:border-primary hover:bg-primary/5 flex h-40 w-full flex-col items-center justify-center gap-2 rounded-3xl border-2 border-dashed border-stone-200 transition-all active:scale-[0.98]"
              onClick={() => inputRef.current?.click()}
            >
              <div className="bg-primary/10 text-primary flex size-12 items-center justify-center rounded-full">
                <ImagePlus className="size-6" />
              </div>
              <div className="text-center">
                <p className="font-bold text-stone-900">เลือกรูปภาพ</p>
                <p className="text-muted-foreground text-xs">{description}</p>
              </div>
            </Button>
          ) : (
            <div className="bg-background ring-border/50 relative flex h-60 w-full justify-center overflow-hidden rounded-3xl p-2 shadow-inner ring-1">
              <img
                src={preview}
                alt="Slip Preview"
                className="size-full object-contain"
              />
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-4 right-4 size-8 rounded-full shadow-lg"
                onClick={handleRemove}
              >
                <X className="size-4" />
              </Button>
            </div>
          )}
        </div>
        <FieldError errors={field.state.meta.errors} />
      </FieldContent>
    </Field>
  )
}
